import * as Yup from 'yup';
import { ETypeOfCompetition } from '../../../components/pages/Registration/DefaultVacancy';
import { validadeFileName, validateMaxFile, validateTypeFile, validationSizeFile } from '../../../utils/validationForm';
import { IDataToRegistration } from '../../../services/hooks/CandidateArea/useGetDataToRegistration';
import { IGetRegistration } from '../../../services/hooks/Registration/useGetRegistration';
import { UseToastOptions } from '@chakra-ui/react';
import { IResponseIdName } from '../../../services/hooks/globalQuery';
import { ISelectiveProcessVancancy } from '../../../services/hooks/SelectiveProcess/useRequestSelectiveProcessVacancy';

interface IShapeFormRegistration {
  listVacancy?: ISelectiveProcessVancancy[];
  selectiveProcess?: IDataToRegistration;
  registration?: IGetRegistration | null;
  toast: (props: UseToastOptions) => void;
  navigate: (route: string) => void;
}

export const shapeFormRegistration = (params: IShapeFormRegistration) => {
  const { selectiveProcess, registration, listVacancy, toast, navigate } = params;

  const verifyIsPcd = (vacancy: string, modality_type: string[], type_of_competition: string) => {
    const idsModalityType = modality_type?.map((item: string) => {
      try {
        const convertObj = JSON.parse(item);
        return convertObj.id;
      } catch (error) {
        return item;
      }
    });

    if (idsModalityType && type_of_competition) {
      const type_of_competition_list =
        listVacancy?.find((item: IResponseIdName) => item.id === vacancy)?.type_of_competition || [];
      const pcdId = type_of_competition_list.find((item: IResponseIdName) => item?.name?.toLowerCase().includes('pcd'));

      return idsModalityType.includes(pcdId?.id) && type_of_competition === ETypeOfCompetition.SPECIAL_VACANCY;
    }

    return false;
  };

  return Yup.object().shape({
    terms: Yup.boolean().test('isRequired', 'É necessario aceitar os termos para continuar', function (accept) {
      const selectiveProcessId = this.options.context?.selectiveProcessId ?? '';
      if (selectiveProcess?.display_terms_acceptance_before_registration) {
        if (!accept) {
          toast({
            title: 'Atenção',
            description: 'É necessario aceitar os termos para continuar',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
          navigate(`/processo-seletivo/informacoes/${selectiveProcessId}`);
          return accept;
        }
      }
      return accept;
    }),
    eligibility_declaration: Yup.boolean().test('isRequired', 'É necessario aceitar para continuar', function (accept) {
      if (selectiveProcess?.show_eligibility_declaration) {
        if (!accept) return false;
      }
      return true;
    }),
    has_second_vacancy: Yup.boolean(),
    type_of_competition: Yup.string().when({
      is: () => !selectiveProcess?.is_cultural_house,
      then: (schema) => schema.required('Campo Obrigatório'),
    }),
    type_of_competition_id: Yup.string(),
    allow_filter_vacancy_tree: Yup.string().when({
      is: () => selectiveProcess?.allow_filter_vacancy_tree,
      then: (schema) => schema.required('Campo Obrigatório'),
    }),
    research_line: Yup.string(),
    thematic_axis: Yup.string(),
    vacancy: Yup.string().test('required', 'Campo Obrigatório', (value) => Boolean(value)),
    culture_home: Yup.string().transform((value) => value ?? '').when({
      is: () => selectiveProcess?.is_cultural_house,
      then: (schema) => schema.required('Campo Obrigatório'),
    }),
    // vagancy_type: Yup.string().when(['culture_home'], {
    //   is: (culture_home: string) => !!culture_home,
    //   then: (schema) => schema.required('O Campo é obrigatorio'),
    // }),
    initial_semester: Yup.string().when(['vagancy_type'], {
      is: (vagancy_type: string) => vagancy_type === 'is_initial_semester',
      then: (schema) => schema.required('O Campo é obrigatorio'),
    }),
    nivel_test: Yup.string().when(['vagancy_type'], {
      is: (vagancy_type: string) => vagancy_type === 'is_level_test',
      then: (schema) => schema.required('O Campo é obrigatorio'),
    }),
    culture_home_secondary: Yup.string().transform((value) => value ?? '').when(['has_second_vacancy'], {
      is: (hasSecondVacancy: boolean) => Boolean(selectiveProcess?.is_cultural_house && hasSecondVacancy),
      then: (schema) => schema.required('O Campo é obrigatorio'),
    }),
    initial_semester_secondary: Yup.string().transform((value) => value ?? '').when(['has_second_vacancy', 'vacancy'], {
      is: (hasSecondVacancy: boolean) => Boolean(selectiveProcess?.is_cultural_house && hasSecondVacancy),
      then: (schema) =>
        schema
          .required('O Campo é obrigatorio')
          .test('Campos iguais', 'Atenção! A sua segunda opção de turma deve ser diferente da primeira.', function () {
            const { vacancy, initial_semester_secondary } = this.parent;
            if (vacancy === initial_semester_secondary) {
              toast({
                title: 'Atenção',
                description: 'Atenção! A sua segunda opção de turma deve ser diferente da primeira.',
                status: 'error',
                duration: 4000,
                isClosable: true,
              });
              return false;
            }
            return true;
          }),
    }),
    juror_art_440_cpp: Yup.string().test('isRequired', 'O campo é obrigatorio', (value) => {
      if (selectiveProcess?.juror_under_article_440 && value?.length == 0) return false;
      return true;
    }),
    weapon_carry_permit: Yup.boolean(),
    medical_report: Yup.mixed().when(['modality_type', 'type_of_competition', 'vacancy'], {
      is: (modality: string[] = [], type: string = '', vacancy: string = '') => verifyIsPcd(vacancy, modality, type),
      then: (schema) =>
        schema
          .test('isRequired', 'Anexação do laudo médico obrigatória', function (file) {
            const listFile = file as FileList;
            const registrationId = this.options.context?.registrationId;
            let validateMedicalReport = (listFile?.length ?? 0) > 0;

            if (registration && registrationId) {
              registration?.reserved_vacancy.forEach((reserved) => {
                if (reserved?.type?.toLowerCase()?.includes('pcd') && reserved?.attachments?.length > 0) {
                  validateMedicalReport = true;
                }
              });
            }

            return validateMedicalReport;
          })
          .test('validationSizeFile', 'Tamanho máximo para arquivos é de 2MB', (file) => {
            const listFile = file as FileList;

            if (listFile?.length > 0) {
              return validationSizeFile(listFile);
            }

            return true;
          })
          .test('validationMaxFile', 'Quantidade máxima de arquivos é de 5 arquivos', (file) => {
            const listFile = file as FileList;

            if (listFile?.length > 0) {
              return validateMaxFile(listFile);
            }
            return true;
          })
          .test('validationTypeFile', 'Formato de arquivo não suportado', (file) => {
            const listFile = file as FileList;

            if (listFile?.length > 0) {
              return validateTypeFile(listFile);
            }
            return true;
          })
          .test('verifyFileName', 'Nome do arquivo invalido', (file) => {
            const listFile = file as FileList;
            if (listFile && listFile.length > 0) {
              return validadeFileName(listFile);
            }
            return true;
          }),
    }),
    cid: Yup.string().when(['modality_type', 'type_of_competition', 'vacancy'], {
      is: (modality: string[] = [], type: string = '', vacancy: string = '') => verifyIsPcd(vacancy, modality, type),
      then: (schema) => schema.required('Campo é obrigatório').max(100, 'Máximo de 100 caracteres'),
      otherwise: (schema) => schema.notRequired(),
    }),
    deficiency: Yup.string().when(['modality_type', 'type_of_competition', 'vacancy'], {
      is: (modality: string[] = [], type: string = '', vacancy: string = '') => verifyIsPcd(vacancy, modality, type),
      then: (schema) => schema.required('Campo é obrigatório').max(5000, 'Máximo de 5000 caracteres'),
      otherwise: (schema) => schema.notRequired(),
    }),
    has_special_condition: Yup.string().when({
      is: () => selectiveProcess?.allows_special_condition,
      then: (schema) => schema.required('O campo é obrigatorio'),
    }),
    has_protocol: Yup.string(),
    modality_type: Yup.array().test('isRequired', 'Selecione ao menos uma modalidade!', function (listModality) {
      if (selectiveProcess?.is_cultural_house) return true;

      const { type_of_competition, vacancy } = this.parent;
      const context = this.options.context;
      const vacancyModality = context?.vacancyList?.find(
        (vac: IResponseIdName) => vac.id === vacancy,
      )?.type_of_competition;

      if (
        !vacancyModality ||
        type_of_competition !== ETypeOfCompetition.SPECIAL_VACANCY ||
        vacancyModality?.length === 0
      )
        return true;

      let isEmpty = false;

      listModality?.forEach((modality) => {
        if (modality !== false) isEmpty = true;
      });

      return isEmpty;
    }),
    sub_type_of_competition: Yup.array()
      .nullable()
      .test('singleOption', 'Selecione apenas uma opção!', function (value) {
        if (!selectiveProcess?.allow_filter_vacancy_tree) return true;
        return (value ?? []).filter(Boolean).length <= 1;
      })
      .test('isRequired', 'Selecione o grupo a que pertence!', function (value) {
        const { modality_type, vacancy } = this.parent;
        if (this.parent.type_of_competition !== ETypeOfCompetition.SPECIAL_VACANCY) return true;

        const context = this.options.context;

        const vacancyModality =
          context?.vacancyList?.find((vac: IResponseIdName) => vac.id === vacancy)?.type_of_competition ?? [];

        const searchModality = vacancyModality?.find((modality: IResponseIdName) =>
          modality.name.toLowerCase().includes('pppiq'),
        );

        let isEmpty = true;

        if (
          searchModality &&
          modality_type &&
          modality_type.length > 0 &&
          (value?.every((v) => v === false) || !value)
        ) {
          modality_type
            .filter((item: string) => item)
            .forEach((item: string) => {
              try {
                const json = JSON.parse(item);
                if (json.id === searchModality.id) isEmpty = false;
              } catch (error) {
                if (item === searchModality.id) isEmpty = false;
              }
            });
        }

        return isEmpty;
      }),
    attachmentFile: Yup.array(),
    protocol_attachmentFile: Yup.array(),
    protocol_photo: Yup.array(),
    observation: Yup.array(),
    social_name_protocol: Yup.array(),
    selectedSpecialCondition: Yup.array()
      .test('isRequired', 'Selecione ao menos um atendimento especial', function (specialConditionList) {
        let isEmpty = false;
        const { has_special_condition } = this.parent;
        if (!(has_special_condition === 'yes')) return true;

        specialConditionList?.forEach((modality) => {
          if (modality !== false) {
            isEmpty = true;
            return;
          }
        });

        return isEmpty;
      })
      .test('isRequired', '', function (specialConditionList) {
        const { has_special_condition, observation: obs } = this.parent;
        if (!(has_special_condition === 'yes')) return true;

        const validationError: Yup.ValidationError[] = [];

        specialConditionList?.forEach((modality, index) => {
          if (modality !== false) {
            const observation: string = obs[index];
            if (observation?.length === 0) {
              if (!selectiveProcess?.special_condition[index].is_social_name) {
                validationError.push(
                  new Yup.ValidationError('Campo Obrigatorio', specialConditionList[index], `observation.${index}`),
                );
              }
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }

        return true;
      })
      .test('isRequired', 'Campo obrigatório', function (specialConditionList) {
        const { attachmentFile } = this.parent;
        const registrationId = this.options.context?.registrationId ?? '';

        if (registrationId) {
          const validationError: Yup.ValidationError[] = [];

          specialConditionList?.forEach((modality, index) => {
            const specialConditionDataToRegistration = selectiveProcess?.special_condition.find(
              (sp) => sp.id === modality,
            );

            if (modality !== false) {
              const file: FileList = attachmentFile[index];
              const spc = registration?.special_conditions.find((sp) => sp?.id === modality);
              if (spc?.attachments && spc?.attachments?.length > 0) {
                if (file?.length === 0 && registration?.special_conditions[index]?.attachments?.length === 0) {
                  if (specialConditionDataToRegistration?.has_attachment) {
                    validationError.push(
                      new Yup.ValidationError(
                        'Selecione um arquivo para continuar',
                        specialConditionList[index],
                        `attachmentFile.${index}`,
                      ),
                    );
                  } else {
                    return true;
                  }
                }

                return true;
              } else if (file?.length === 0 && specialConditionDataToRegistration?.has_attachment) {
                validationError.push(
                  new Yup.ValidationError(
                    'Selecione um arquivo para continuar',
                    specialConditionList.filter((sp) => sp !== false)[index],
                    `attachmentFile.${index}`,
                  ),
                );
                return;
              }
            }
          });
          if (validationError?.length > 0) {
            throw new Yup.ValidationError(validationError);
          }
          return true;
        }

        if (!registrationId) {
          const validationError: Yup.ValidationError[] = [];
          specialConditionList?.forEach((modality, index) => {
            const specialConditionDataToRegistration = selectiveProcess?.special_condition.find(
              (sp) => sp.id === modality,
            );
            if (modality !== false) {
              const file: FileList = attachmentFile[index];
              if (file?.length === 0 && specialConditionDataToRegistration?.has_attachment) {
                validationError.push(
                  new Yup.ValidationError(
                    'Selecione um arquivo para continuar',
                    specialConditionList[index],
                    `attachmentFile.${index}`,
                  ),
                );
              }
            }
          });
          if (validationError?.length > 0) {
            throw new Yup.ValidationError(validationError);
          }
          return true;
        }
      })
      .test('TypeFile', 'Tipo de arquivo nao suportado', function (specialConditionList) {
        const { attachmentFile } = this.parent;
        if (!specialConditionList || (specialConditionList && specialConditionList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];
        specialConditionList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const file: FileList = attachmentFile[index];
            if (file?.length > 0) {
              const response = validateTypeFile(file);
              if (!response) {
                validationError.push(
                  new Yup.ValidationError(
                    'Tipo de arquivo nao suportado',
                    specialConditionList[index],
                    `attachmentFile.${index}`,
                  ),
                );
              }
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      })
      .test('MaxFileSize', '', function (specialConditionList) {
        const { attachmentFile } = this.parent;
        if (!specialConditionList || (specialConditionList && specialConditionList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];
        specialConditionList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const file: FileList = attachmentFile[index];
            if (file?.length > 0) {
              const response = validationSizeFile(file);
              if (!response) {
                validationError.push(
                  new Yup.ValidationError(
                    'Tamanho máximo para arquivos é de 2MB',
                    specialConditionList[index],
                    `attachmentFile.${index}`,
                  ),
                );
              }
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      })
      .test('MaxFile', 'Quantidade maxima de arquivos atingida (5)', function (specialConditionList) {
        const { attachmentFile } = this.parent;
        if (!specialConditionList || (specialConditionList && specialConditionList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];

        specialConditionList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const attach = attachmentFile[index];
            if (attach && attach?.length > 0) {
              registration?.special_conditions.forEach((value) => {
                if (value.id === modalityListFile) {
                  const file = value?.attachments?.length || 0;
                  if (file > 4) {
                    validationError.push(
                      new Yup.ValidationError(
                        'Quantidade maxima de arquivos atingida (5)',
                        specialConditionList[index],
                        `attachmentFile.${index}`,
                      ),
                    );
                  }
                }
              });
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      })
      .test('FileName', 'Nome de arquivo invalido', function (specialConditionList) {
        const { attachmentFile } = this.parent;
        if (!specialConditionList || (specialConditionList && specialConditionList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];

        specialConditionList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const attach = attachmentFile[index];
            if (attach?.length > 0) {
              const file = validadeFileName(attach);

              if (!file) {
                validationError.push(
                  new Yup.ValidationError(
                    'Nome do arquivo invalido',
                    specialConditionList[index],
                    `attachmentFile.${index}`,
                  ),
                );
              }
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      }),
    selectedProtocol: Yup.array()
      // .test('isRequired', 'Selecione ao menos um protocolo', (protocolList) => {
      //   let isEmpty = false;
      //   //
      //   if (!(getValues('has_protocol') === 'yes')) return true;

      //   protocolList?.forEach((modality) => {
      //     if (modality !== false) {
      //       isEmpty = true;
      //       return;
      //     }
      //   });

      //   return isEmpty;
      // })
      .test('isRequired', '', function (protocolList) {
        const { social_name_protocol } = this.parent;
        // if (!(getValues('has_protocol') === 'yes')) return true;

        const validationError: Yup.ValidationError[] = [];
        protocolList?.forEach((modality, index) => {
          if (modality !== false) {
            const social_name: string = social_name_protocol[index];
            if (social_name?.length === 0) {
              validationError.push(
                new Yup.ValidationError('Campo Obrigatorio', protocolList[index], `social_name_protocol.${index}`),
              );
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }

        return true;
      })
      .test('isRequired', 'Campo obrigatório', function (protocolList) {
        const { protocol_attachmentFile } = this.parent;
        const registrationId = this.options.context?.registrationId ?? '';

        if (registrationId) {
          const validationError: Yup.ValidationError[] = [];

          protocolList?.forEach((modality, index) => {
            if (modality !== false) {
              const file: FileList = protocol_attachmentFile[index];
              const spc = registration?.protocol.find((sp) => sp?.protocol_type.id === modality);
              if (spc?.attachments && spc?.attachments?.length > 0) {
                if (file?.length === 0 && registration?.protocol[index]?.attachments?.length === 0) {
                  validationError.push(
                    new Yup.ValidationError(
                      'Selecione um arquivo para continuar',
                      protocolList[index],
                      `protocol_attachmentFile.${index}`,
                    ),
                  );
                }

                return true;
              } else if (file?.length === 0) {
                validationError.push(
                  new Yup.ValidationError(
                    'Selecione um arquivo para continuar',
                    protocolList.filter((sp) => sp !== false)[index],
                    `protocol_attachmentFile.${index}`,
                  ),
                );
                return;
              }
            }
          });
          if (validationError?.length > 0) {
            throw new Yup.ValidationError(validationError);
          }
          return true;
        }

        if (!registrationId) {
          const validationError: Yup.ValidationError[] = [];
          protocolList?.forEach((modality, index) => {
            if (modality !== false) {
              const file: FileList = protocol_attachmentFile[index];
              if (file?.length === 0) {
                validationError.push(
                  new Yup.ValidationError(
                    'Selecione um arquivo para continuar',
                    protocolList[index],
                    `protocol_attachmentFile.${index}`,
                  ),
                );
              }
            }
          });
          if (validationError?.length > 0) {
            throw new Yup.ValidationError(validationError);
          }
          return true;
        }
      })
      .test('TypeFile', 'Tipo de arquivo nao suportado', function (protocolList) {
        const { protocol_attachmentFile } = this.parent;
        if (!protocolList || (protocolList && protocolList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];
        protocolList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const file: FileList = protocol_attachmentFile[index];
            if (file?.length > 0) {
              const response = validateTypeFile(file);
              if (!response) {
                validationError.push(
                  new Yup.ValidationError(
                    'Tipo de arquivo nao suportado',
                    protocolList[index],
                    `protocol_attachmentFile.${index}`,
                  ),
                );
              }
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      })
      .test('MaxFileSize', '', function (protocolList) {
        const { protocol_attachmentFile } = this.parent;
        if (!protocolList || (protocolList && protocolList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];
        protocolList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const file: FileList = protocol_attachmentFile[index];
            if (file?.length > 0) {
              const response = validationSizeFile(file);
              if (!response) {
                validationError.push(
                  new Yup.ValidationError(
                    'Tamanho máximo para arquivos é de 2MB',
                    protocolList[index],
                    `protocol_attachmentFile.${index}`,
                  ),
                );
              }
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      })
      .test('MaxFile', 'Quantidade maxima de arquivos atingida (5)', function (protocolList) {
        const { protocol_attachmentFile } = this.parent;
        if (!protocolList || (protocolList && protocolList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];

        protocolList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const attach = protocol_attachmentFile[index];
            if (attach && attach?.length > 0) {
              registration?.special_conditions.forEach((value) => {
                if (value.id === modalityListFile) {
                  const file = value?.attachments?.length || 0;
                  if (file > 4) {
                    validationError.push(
                      new Yup.ValidationError(
                        'Quantidade maxima de arquivos atingida (5)',
                        protocolList[index],
                        `protocol_attachmentFile.${index}`,
                      ),
                    );
                  }
                }
              });
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      })
      .test('FileName', 'Nome de arquivo invalido', function (protocolList) {
        const { protocol_attachmentFile } = this.parent;
        if (!protocolList || (protocolList && protocolList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];

        protocolList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const attach = protocol_attachmentFile[index];
            if (attach?.length > 0) {
              const file = validadeFileName(attach);

              if (!file) {
                validationError.push(
                  new Yup.ValidationError(
                    'Nome do arquivo invalido',
                    protocolList[index],
                    `protocol_attachmentFile.${index}`,
                  ),
                );
              }
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      })
      .test('isRequired', 'Campo obrigatório', function (protocolList) {
        const { protocol_photo } = this.parent;
        const registrationId = this.options.context?.registrationId ?? '';

        if (registrationId) {
          const validationError: Yup.ValidationError[] = [];

          protocolList?.forEach((modality, index) => {
            if (modality !== false) {
              const file: FileList = protocol_photo[index];
              const spc = registration?.protocol.find((sp) => sp?.protocol_type.id === modality);
              if (spc?.photo) {
                if (file?.length === 0 && registration?.protocol[index]?.attachments?.length === 0) {
                  validationError.push(
                    new Yup.ValidationError(
                      'Selecione um arquivo para continuar',
                      protocolList[index],
                      `protocol_photo.${index}`,
                    ),
                  );
                }

                return true;
              } else if (file?.length === 0) {
                validationError.push(
                  new Yup.ValidationError(
                    'Selecione um arquivo para continuar',
                    protocolList.filter((sp) => sp !== false)[index],
                    `protocol_photo.${index}`,
                  ),
                );
                return;
              }
            }
          });
          if (validationError?.length > 0) {
            throw new Yup.ValidationError(validationError);
          }
          return true;
        }

        if (!registrationId) {
          const validationError: Yup.ValidationError[] = [];
          protocolList?.forEach((modality, index) => {
            if (modality !== false) {
              const file: FileList = protocol_photo[index];
              if (file?.length === 0) {
                validationError.push(
                  new Yup.ValidationError(
                    'Selecione um arquivo para continuar',
                    protocolList[index],
                    `protocol_photo.${index}`,
                  ),
                );
              }
            }
          });
          if (validationError?.length > 0) {
            throw new Yup.ValidationError(validationError);
          }
          return true;
        }
      })
      .test('TypeFile', 'Tipo de arquivo nao suportado', function (protocolList) {
        const { protocol_photo } = this.parent;
        if (!protocolList || (protocolList && protocolList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];
        protocolList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const file: FileList = protocol_photo[index];
            if (file?.length > 0) {
              const response = validateTypeFile(file);
              if (!response) {
                validationError.push(
                  new Yup.ValidationError(
                    'Tipo de arquivo nao suportado',
                    protocolList[index],
                    `protocol_photo.${index}`,
                  ),
                );
              }
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      })
      .test('MaxFileSize', '', function (protocolList) {
        const { protocol_photo } = this.parent;
        if (!protocolList || (protocolList && protocolList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];
        protocolList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const file: FileList = protocol_photo[index];
            if (file?.length > 0) {
              const response = validationSizeFile(file);
              if (!response) {
                validationError.push(
                  new Yup.ValidationError(
                    'Tamanho máximo para arquivos é de 2MB',
                    protocolList[index],
                    `protocol_photo.${index}`,
                  ),
                );
              }
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      })
      .test('MaxFile', 'Quantidade maxima de arquivos atingida (5)', function (protocolList) {
        const { protocol_photo } = this.parent;
        if (!protocolList || (protocolList && protocolList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];

        protocolList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const attach = protocol_photo[index];
            if (attach && attach?.length > 0) {
              registration?.special_conditions.forEach((value) => {
                if (value.id === modalityListFile) {
                  const file = value?.attachments?.length || 0;
                  if (file > 4) {
                    validationError.push(
                      new Yup.ValidationError(
                        'Quantidade maxima de arquivos atingida (5)',
                        protocolList[index],
                        `protocol_photo.${index}`,
                      ),
                    );
                  }
                }
              });
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      })
      .test('FileName', 'Nome de arquivo invalido', function (protocolList) {
        const { protocol_photo } = this.parent;
        if (!protocolList || (protocolList && protocolList.length === 0)) return true;

        const validationError: Yup.ValidationError[] = [];

        protocolList?.forEach((modalityListFile, index) => {
          if (modalityListFile !== false) {
            const attach = protocol_photo[index];
            if (attach?.length > 0) {
              const file = validadeFileName(attach);

              if (!file) {
                validationError.push(
                  new Yup.ValidationError('Nome do arquivo invalido', protocolList[index], `protocol_photo.${index}`),
                );
              }
            }
          }
        });
        if (validationError.length > 0) {
          throw new Yup.ValidationError(validationError);
        }
        return true;
      }),
  });
};
